import { useState, useMemo } from 'react'
import { router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Combobox } from '@/components/ui/combobox'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { Check, ArrowRight, DollarSign, Video, MapPin } from 'lucide-react'
import type { NotarizationTypeOption } from '@/types'

type BusinessOption = { id: string; name: string; country_id: string | null; state_id: string | null }

type Props = {
  countries: { id: string; code: string; name: string }[]
  states: { id: string; code: string; name: string; country_id: string }[]
  types: NotarizationTypeOption[]
  businesses: BusinessOption[]
}

export default function NotarizationNew({ countries, states, types, businesses }: Props) {
  const basePath = useBasePath()
  const [selectedCountryId, setSelectedCountryId] = useState('')
  const [selectedStateId, setSelectedStateId] = useState('')
  const [selectedBusinessId, setSelectedBusinessId] = useState('')
  const [selectedTypeId, setSelectedTypeId] = useState('')
  const [selectedSessionMode, setSelectedSessionMode] = useState('')
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const countryStates = states.filter(s => s.country_id === selectedCountryId)
  const hasStates = countryStates.length > 0

  const availableTypes = useMemo(() => {
    if (!selectedCountryId) return []
    return types.filter(t => {
      if (t.country_id !== selectedCountryId) return false
      if (t.state_id) {
        if (!selectedStateId) return false
        if (t.state_id !== selectedStateId) return false
      }
      return true
    })
  }, [types, selectedCountryId, selectedStateId])

  const selectedType = types.find(t => t.id === selectedTypeId)

  const availableSessionModes = useMemo(() => {
    if (!selectedType) return []
    return selectedType.session_modes
  }, [selectedType])

  const sessionModeConfig: Record<string, { label: string; description: string; icon: React.ReactNode }> = {
    in_person: { label: 'In Person', description: 'Meet the notary at a physical location', icon: <MapPin className="w-4 h-4" /> },
    ron: { label: 'Remote Online (RON)', description: 'Complete the session via secure video call', icon: <Video className="w-4 h-4" /> },
  }

  const handleSubmit = () => {
    if (!selectedBusinessId || !selectedTypeId || !selectedSessionMode) return
    setIsSubmitting(true)
    router.post(`${basePath}/notarizations`, {
      notarization: {
        business_id: selectedBusinessId,
        notarization_type_id: selectedTypeId,
        session_mode: selectedSessionMode,
        notes: notes.trim() || null,
      }
    }, { onFinish: () => setIsSubmitting(false) })
  }

  const stepNumber = (base: number) => base

  return (
    <DashboardLayout title="New Notarization" breadcrumbs={[{ label: 'Notarizations', href: 'notarizations' }, { label: 'New Notarization' }]}>
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">New Notarization</h1>
        <p className="text-sm text-muted-foreground">Choose your business and notarization type</p>
      </div>
      <div className="space-y-6">
        {/* Step 1: Business */}
        <section>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">1</span>
            <h2 className="text-base font-medium text-foreground">Business</h2>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Select business</Label>
            <Combobox
              options={businesses.map(b => ({ value: b.id, label: b.name }))}
              value={selectedBusinessId}
              onValueChange={(value) => {
                setSelectedBusinessId(value)
                const biz = businesses.find(b => b.id === value)
                if (biz?.country_id) {
                  setSelectedCountryId(biz.country_id)
                  setSelectedStateId(biz.state_id || '')
                } else {
                  setSelectedCountryId('')
                  setSelectedStateId('')
                }
                setSelectedTypeId('')
                setSelectedSessionMode('')
              }}
              placeholder="Select a business..."
              searchPlaceholder="Search businesses..."
              emptyMessage="No businesses found."
            />
          </div>
        </section>

        {/* Step 2: Jurisdiction */}
        {selectedBusinessId && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">2</span>
              <h2 className="text-base font-medium text-foreground">Jurisdiction</h2>
            </div>
            {(() => {
              const biz = businesses.find(b => b.id === selectedBusinessId)
              if (biz?.country_id) {
                const country = countries.find(c => c.id === biz.country_id)
                const state = states.find(s => s.id === biz.state_id)
                return (
                  <div className="px-3 py-2.5 bg-muted rounded-lg text-sm">
                    <span className="text-muted-foreground">Auto-selected: </span>
                    <span className="font-medium">{[state?.name, country?.name].filter(Boolean).join(', ')}</span>
                  </div>
                )
              }
              return (
                <div className={`grid gap-3 ${hasStates ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Country</Label>
                    <Combobox
                      options={countries.map(c => ({ value: c.id, label: c.name }))}
                      value={selectedCountryId}
                      onValueChange={(v) => { setSelectedCountryId(v); setSelectedStateId(''); setSelectedTypeId(''); setSelectedSessionMode('') }}
                      placeholder="Select country..."
                      searchPlaceholder="Search countries..."
                      emptyMessage="No countries found."
                    />
                  </div>
                  {hasStates && (
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">State</Label>
                      <Combobox
                        options={countryStates.map(s => ({ value: s.id, label: s.name }))}
                        value={selectedStateId}
                        onValueChange={(v) => { setSelectedStateId(v); setSelectedTypeId(''); setSelectedSessionMode('') }}
                        placeholder="Select state..."
                        searchPlaceholder="Search states..."
                        emptyMessage="No states found."
                      />
                    </div>
                  )}
                </div>
              )
            })()}
          </section>
        )}

        {/* Step 3: Notarization Type */}
        {availableTypes.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">3</span>
              <h2 className="text-base font-medium text-foreground">Notarization Type</h2>
            </div>
            <div className="space-y-1.5">
              {availableTypes.map((t) => {
                const isSelected = selectedTypeId === t.id
                return (
                  <button key={t.id} type="button"
                    onClick={() => { setSelectedTypeId(t.id); setSelectedSessionMode('') }}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${isSelected ? 'border-foreground bg-muted' : 'border-border hover:border-border'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{t.name}</span>
                          {isSelected && <Check className="w-4 h-4 text-foreground" />}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{formatCurrency(t.total_cents)}</span>
                          <span>{t.session_modes.map(m => m === 'ron' ? 'RON' : 'In Person').join(', ')}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </section>
        )}

        {/* Step 4: Session Mode */}
        {selectedType && availableSessionModes.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">4</span>
              <h2 className="text-base font-medium text-foreground">Session Mode</h2>
            </div>
            <div className="space-y-1.5">
              {availableSessionModes.map((mode) => {
                const isSelected = selectedSessionMode === mode
                const config = sessionModeConfig[mode] || { label: mode, description: '', icon: null }
                return (
                  <button key={mode} type="button"
                    onClick={() => setSelectedSessionMode(mode)}
                    className={`w-full p-3 text-left rounded-lg border transition-all ${isSelected ? 'border-foreground bg-muted' : 'border-border hover:border-border'}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0 text-muted-foreground">
                        {config.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{config.label}</span>
                          {isSelected && <Check className="w-4 h-4 text-foreground" />}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{config.description}</p>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </section>
        )}

        {/* Step 5: Notes */}
        {selectedSessionMode && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">5</span>
              <h2 className="text-base font-medium text-foreground">Notes (Optional)</h2>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Additional notes or instructions</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special requirements or instructions..."
                rows={3}
              />
            </div>
          </section>
        )}

        {/* Step 6: Review & Submit */}
        {selectedType && selectedBusinessId && selectedSessionMode && (
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">6</span>
              <h2 className="text-base font-medium text-foreground">Review & Submit</h2>
            </div>
            <div className="bg-muted rounded-xl p-4 space-y-3">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{selectedType.name}</span>
                  <span className="font-medium">{formatCurrency(selectedType.total_cents)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground pl-4 text-xs">
                  <span>Service Fee</span>
                  <span>{formatCurrency(selectedType.service_fee_cents)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground pl-4 text-xs">
                  <span>Government Fee</span>
                  <span>{formatCurrency(selectedType.gov_fee_cents)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground pl-4 text-xs">
                  <span>Session Mode</span>
                  <span>{selectedSessionMode === 'ron' ? 'Remote Online (RON)' : 'In Person'}</span>
                </div>
                <div className="h-px bg-border" />
                <div className="flex justify-between font-medium">
                  <span>Total</span>
                  <span>{formatCurrency(selectedType.total_cents)}</span>
                </div>
              </div>
              <Button onClick={handleSubmit} className="w-full h-9 text-sm" disabled={!selectedBusinessId || !selectedTypeId || !selectedSessionMode || isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Continue'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </section>
        )}
      </div>
    </DashboardLayout>
  )
}
